/* eslint-disable max-lines, react/jsx-props-no-spreading */
import React, { Suspense, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Switch, useHistory } from 'react-router-dom'
import styled from '@emotion/styled'
import { DESKTOP_BREAKPOINT, useTheme, Card } from '@xfers/design-system'

import PageLoader from '~/components/PageLoader'
import { RouteHooks, PrivateRoute } from '~/components/Route'
import { createGaEvent, createGaPageView } from '~/helpers/gaHelper'
import { init } from '~/biz/store/route/actions'
import { RootState } from '~/biz/store/types'
import { withPermission } from '~/permission'
import { PERMISSIONS } from '~/types'
import routes from '~/biz/routes/config'
import AccessDenied from '~/components/AccessDenied'
import Footer from '~/components/Footer'
import Navigation from '~/components/Navigation'
import UserInfoBar from '~/components/UserInfoBar'
import { setIsSandbox } from '~/biz/store/sandbox/actions'
import { getFeatureMatrixFactors } from './store/route/selectors'

const EnhancedRoute = ({ permission, ...props }: { permission?: PERMISSIONS }) => {
  const WrappedRoute = permission
    ? withPermission(PrivateRoute, permission, AccessDenied)
    : PrivateRoute
  return <WrappedRoute {...props} />
}

export default () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { isInitiated } = useSelector((state: RootState) => state.route)
  const isSandbox = useSelector((state: RootState) => state.sandbox.isSandbox)
  const user = useSelector((state: RootState) => state.route.user)
  const notifications = useSelector((state: RootState) => state.route.notifications)
  const { role } = useSelector(getFeatureMatrixFactors)

  history.listen(location => {
    const urlPath = location.pathname + location.search
    createGaPageView(urlPath)
    createGaEvent('URL', urlPath, 'Changed URL')
  })

  useEffect(() => {
    dispatch(init())
  }, [isInitiated]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isInitiated) {
    return <PageLoader />
  }

  return (
    <Root id="biz-root">
      <RouteHooks />

      <Container>
        <NavigationWrapper>
          <Navigation isSandbox={isSandbox} />
        </NavigationWrapper>
        <MainContainer>
          <UserInfoBar
            userInfo={user}
            isSandbox={isSandbox}
            switchSandbox={(sandboxMode: boolean) => dispatch(setIsSandbox(sandboxMode))}
            notifications={notifications}
            role={role}
          />

          <Suspense fallback={<LoadingPage />}>
            <Switch>
              {routes.map((route, i) => (
                <EnhancedRoute key={i} {...route} />
              ))}
            </Switch>
          </Suspense>

          <Footer />
        </MainContainer>
      </Container>
    </Root>
  )
}

function LoadingPage() {
  const { spacing } = useTheme()

  return (
    <Page>
      <Card style={{ padding: spacing.xl }}>
        <Skeleton style={{ width: '40%' }} />
        <Skeleton />
        <Skeleton />
      </Card>
    </Page>
  )
}

const Page = styled.div`
  padding: 16px;
  background: none;

  ${DESKTOP_BREAKPOINT} {
    background-color: #f6f7f9;
    padding: 24px;
  }
`

const Root = styled.div`
  z-index: 0;
`

const Container = styled.div`
  background-color: #ffffff;
  -webkit-font-smoothing: antialiased;
  display: flex;
`

const MainContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #ffffff;
  padding-top: 73px;

  ${DESKTOP_BREAKPOINT} {
    padding-top: 0;
  }
`

const NavigationWrapper = styled.div`
  display: none;

  ${DESKTOP_BREAKPOINT} {
    display: flex;
    width: 257px;
    flex-shrink: 0;
  }
`

const Skeleton = styled.div`
  float: left;
  width: 100%;
  height: 16px;

  margin: 10px;
  border-radius: 5px;
  background-size: 100wh;
  background-image: linear-gradient(90deg, #e8e8e8 0%, #f6f7f9 50%, #e8e8e8 100%);
  animation: shine-lines 1s infinite linear;

  @keyframes shine-lines {
    0% {
      background-position: -100px;
    }
    40%,
    100% {
      background-position: 140px;
    }
  }
`
